import type { ReportRow } from '@/store/slices/reportingV2/reportingV2.api';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { entityLabel, num, percent } from '../format';
import { useReportWidget } from '../hooks/useReportWidget';
import { COLORS, cardSx } from '../theme';
import { ROWS_PER_PAGE_OPTIONS, paginateRows } from './Conversions';
import { pageWindow } from './ImpressionsByType';
import WidgetSkeleton from './WidgetSkeleton';

const LINE_ITEM_KEY = 'dv360LineItemPerformance';
const CREATIVE_KEY = 'dv360CreativePerformance';

// Same 20/50/100 toggle styling Conversions.tsx/ImpressionsByType.tsx/
// PublisherInventoryTable.tsx each keep their own copy of, rather than a
// shared component.
const darkToggleSx = {
  bgcolor: COLORS.pageBg,
  borderRadius: '10px',
  border: `1px solid ${COLORS.border}`,
  p: 0.5,
  '& .MuiToggleButton-root': {
    textTransform: 'none',
    border: 0,
    borderRadius: '8px !important',
    px: 1.5,
    py: 0.25,
    color: COLORS.muted,
    fontSize: 13,
    fontWeight: 600,
    '&.Mui-selected': { bgcolor: COLORS.black, color: '#fff', '&:hover': { bgcolor: '#000' } },
  },
} as const;

/** Rows/Prev-Next footer shared by the Line Item and Creative tables below —
 * same look/feel as every other paginated Reporting V2 grid (flank#5149
 * follow-up: this card was the one grid on the page still rendering every
 * row unpaginated). */
const PaginationFooter = ({
  total,
  rowsPerPage,
  safePage,
  pageCount,
  shown,
  onPageChange,
  onRowsPerPageChange,
}: {
  total: number;
  rowsPerPage: number;
  safePage: number;
  pageCount: number;
  shown: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
}) => {
  if (total <= ROWS_PER_PAGE_OPTIONS[0]) return null;
  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, pt: 1 }}
    >
      <Typography variant='caption' sx={{ color: COLORS.muted }}>
        {(safePage * rowsPerPage + 1).toLocaleString()}–{(safePage * rowsPerPage + shown).toLocaleString()} of{' '}
        {total.toLocaleString()} rows
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography variant='caption' sx={{ color: COLORS.muted }}>
            Rows
          </Typography>
          <ToggleButtonGroup
            size='small'
            exclusive
            value={rowsPerPage}
            onChange={(_e, v) => v && onRowsPerPageChange(v)}
            sx={darkToggleSx}
          >
            {ROWS_PER_PAGE_OPTIONS.map(n => (
              <ToggleButton key={n} value={n}>
                {n}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        <Box className='rv2-noprint' sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Button
            size='small'
            disabled={safePage === 0}
            onClick={() => onPageChange(Math.max(0, safePage - 1))}
            sx={{ textTransform: 'none', minWidth: 0, color: COLORS.muted }}
          >
            Prev
          </Button>
          {pageWindow(safePage, pageCount).map((n, i) =>
            n === -1 ? (
              <Typography key={`gap-${i}`} variant='caption' sx={{ color: COLORS.muted, px: 0.5 }}>
                …
              </Typography>
            ) : (
              <Button
                key={n}
                size='small'
                onClick={() => onPageChange(n)}
                sx={{
                  textTransform: 'none',
                  minWidth: 32,
                  px: 1,
                  color: n === safePage ? '#fff' : COLORS.muted,
                  bgcolor: n === safePage ? COLORS.black : 'transparent',
                  '&:hover': { bgcolor: n === safePage ? '#000' : COLORS.pageBg },
                }}
              >
                {n + 1}
              </Button>
            ),
          )}
          <Button
            size='small'
            disabled={safePage >= pageCount - 1}
            onClick={() => onPageChange(Math.min(pageCount - 1, safePage + 1))}
            sx={{ textTransform: 'none', minWidth: 0, color: COLORS.muted }}
          >
            Next
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

/**
 * The global Platform filter can be set to anything; this card is always
 * DV360 only, same scope as flank#4044. The request itself is scoped via
 * `dspsOverride` below — this filter is a belt-and-braces guard on the
 * fetched rows, not the scoping mechanism (same pattern as googleAdsRows in
 * GoogleAdsCampaignBreakdownCard).
 */
export const dv360Rows = (rows: ReportRow[]): ReportRow[] => rows.filter(row => row.dsp === 'dv360');

/**
 * Review, Lucas on #4044: DV360 has 1,639 creatives over 30 days, above even
 * a correctly DV360-scoped 1,000-row limit — the table would still silently
 * truncate. A note naming the cap, rather than raising the limit until it
 * hopefully stays ahead of DV360's own growth, stays correct regardless of
 * volume.
 */
export const truncationNote = (shown: number, total: number): string | null =>
  total > shown ? `Showing the top ${shown.toLocaleString()} of ${total.toLocaleString()} by impressions.` : null;

const LINE_ITEM_COLUMNS: { key: string; label: string; align?: 'right' }[] = [
  { key: 'insertion_order', label: 'Insertion Order' },
  { key: 'line_item', label: 'Line Item' },
  { key: 'impressions', label: 'Impressions', align: 'right' },
  { key: 'clicks', label: 'Clicks', align: 'right' },
  { key: 'ctr', label: 'CTR', align: 'right' },
  { key: 'conversions', label: 'Total Conversions', align: 'right' },
];

const CREATIVE_COLUMNS: { key: string; label: string; align?: 'right' }[] = [
  { key: 'creative', label: 'Creative' },
  { key: 'line_item', label: 'Line Item' },
  { key: 'impressions', label: 'Impressions', align: 'right' },
  { key: 'clicks', label: 'Clicks', align: 'right' },
  { key: 'conversions', label: 'Total Conversions', align: 'right' },
];

const Dv360CreativeLineItemPerformanceCard = () => {
  const lineItems = useReportWidget(LINE_ITEM_KEY, {
    // DV360's own hierarchy names `campaign` the Insertion Order (confirmed:
    // GoogleReportCsvHeader.InsertionOrder maps to CAMPAIGN_NAME in
    // app/Constants/DV360/parsing.ts) — no separate canonical dimension needed.
    dimensions: ['dsp', 'campaign', 'campaign_name', 'line_item', 'line_item_name'],
    metrics: ['impressions', 'clicks', 'ctr', 'conversions'],
    sort: [{ field: 'impressions', dir: 'desc' }],
    limit: 1000,
    // Review, Lucas on #4044: without this, the limit applies across every
    // platform first (DV360 was 96 of 769 line items, 12%) before the
    // client-side dv360Rows filter ever runs.
    dspsOverride: ['dv360'],
  });
  const creatives = useReportWidget(CREATIVE_KEY, {
    dimensions: ['dsp', 'creative', 'creative_name', 'line_item', 'line_item_name'],
    metrics: ['impressions', 'clicks', 'conversions'],
    sort: [{ field: 'impressions', dir: 'desc' }],
    // DV360 alone has 1,639 creatives — above even the server's own hard cap on
    // `pagination.limit` (ReportValidator.ts, range 1–1000; a higher value 422s
    // the whole request, not just this widget's batch — confirmed on preprod).
    // `truncationNote` below names the truncation rather than presenting a
    // partial table as complete.
    limit: 1000,
    dspsOverride: ['dv360'],
  });

  const lineItemRows = dv360Rows(lineItems.rows);
  const creativeRows = dv360Rows(creatives.rows);

  const [lineItemPage, setLineItemPage] = useState(0);
  const [lineItemRowsPerPage, setLineItemRowsPerPage] = useState<number>(ROWS_PER_PAGE_OPTIONS[0]);
  const lineItemPaged = paginateRows(lineItemRows, lineItemPage, lineItemRowsPerPage);

  const [creativePage, setCreativePage] = useState(0);
  const [creativeRowsPerPage, setCreativeRowsPerPage] = useState<number>(ROWS_PER_PAGE_OPTIONS[0]);
  const creativePaged = paginateRows(creativeRows, creativePage, creativeRowsPerPage);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ ...cardSx, p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant='subtitle1' sx={{ fontWeight: 600, color: COLORS.text }}>
          DV360 — Line Item Performance
        </Typography>
        {lineItems.loading ? (
          <WidgetSkeleton height={280} />
        ) : lineItems.error ? (
          <Typography variant='body2' color='error'>
            {lineItems.error}
          </Typography>
        ) : lineItemRows.length === 0 ? (
          <Typography variant='body2' color='text.secondary'>
            No DV360 line items for the selected range yet.
          </Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  {LINE_ITEM_COLUMNS.map(col => (
                    <TableCell key={col.key} align={col.align} sx={{ color: COLORS.muted, fontWeight: 600 }}>
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {lineItemPaged.pageRows.map(row => (
                  <TableRow key={String(row.line_item)}>
                    <TableCell>{entityLabel(row.campaign_name, row.campaign, 'insertion order')}</TableCell>
                    <TableCell>{entityLabel(row.line_item_name, row.line_item, 'line item')}</TableCell>
                    <TableCell align='right'>{num(row.impressions)}</TableCell>
                    <TableCell align='right'>{num(row.clicks)}</TableCell>
                    <TableCell align='right'>{percent(row.ctr)}</TableCell>
                    <TableCell align='right'>{num(row.conversions)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
        <PaginationFooter
          total={lineItemRows.length}
          rowsPerPage={lineItemRowsPerPage}
          safePage={lineItemPaged.safePage}
          pageCount={lineItemPaged.pageCount}
          shown={lineItemPaged.pageRows.length}
          onPageChange={setLineItemPage}
          onRowsPerPageChange={next => {
            setLineItemRowsPerPage(next);
            setLineItemPage(0);
          }}
        />
        {truncationNote(lineItemRows.length, lineItems.totalRows) && (
          <Typography variant='caption' color='text.secondary'>
            {truncationNote(lineItemRows.length, lineItems.totalRows)}
          </Typography>
        )}
      </Box>

      <Box sx={{ ...cardSx, p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant='subtitle1' sx={{ fontWeight: 600, color: COLORS.text }}>
          DV360 — Creative Performance
        </Typography>
        {creatives.loading ? (
          <WidgetSkeleton height={280} />
        ) : creatives.error ? (
          <Typography variant='body2' color='error'>
            {creatives.error}
          </Typography>
        ) : creativeRows.length === 0 ? (
          <Typography variant='body2' color='text.secondary'>
            No DV360 creatives for the selected range yet.
          </Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  {CREATIVE_COLUMNS.map(col => (
                    <TableCell key={col.key} align={col.align} sx={{ color: COLORS.muted, fontWeight: 600 }}>
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {creativePaged.pageRows.map(row => (
                  <TableRow key={`${String(row.creative)}-${String(row.line_item)}`}>
                    <TableCell>{entityLabel(row.creative_name, row.creative, 'creative')}</TableCell>
                    <TableCell>{entityLabel(row.line_item_name, row.line_item, 'line item')}</TableCell>
                    <TableCell align='right'>{num(row.impressions)}</TableCell>
                    <TableCell align='right'>{num(row.clicks)}</TableCell>
                    <TableCell align='right'>{num(row.conversions)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
        <PaginationFooter
          total={creativeRows.length}
          rowsPerPage={creativeRowsPerPage}
          safePage={creativePaged.safePage}
          pageCount={creativePaged.pageCount}
          shown={creativePaged.pageRows.length}
          onPageChange={setCreativePage}
          onRowsPerPageChange={next => {
            setCreativeRowsPerPage(next);
            setCreativePage(0);
          }}
        />
        {truncationNote(creativeRows.length, creatives.totalRows) && (
          <Typography variant='caption' color='text.secondary'>
            {truncationNote(creativeRows.length, creatives.totalRows)}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default Dv360CreativeLineItemPerformanceCard;
